import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {Box, HStack, Text} from '@chakra-ui/react'
import {statusLevels} from '../../pages/account/accountModalConstant'
import {useCustomer, useCustomerId, useCustomerType} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useBrand} from '../../contexts/brand-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

const LoyaltyPoints = ({isAccountDrawer, showGauge = false, loyaltyId = null}) => {
    const isGarage = useBrand()
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const intl = useIntl()
    const customerId = useCustomerId()
    const {isRegistered} = useCustomerType()
    const customer = useCustomer(
        {parameters: {customerId}},
        {enabled: isRegistered && !!customerId}
    )

    // Prevent hydration mismatch - customer data only available on client
    const [hasMounted, setHasMounted] = useState(false)
    useEffect(() => {
        setHasMounted(true)
    }, [])

    if (getSiteEnvVarValue(appConfig, 'SFLM_ENABLED', siteId) !== 'true') {
        return null
    }

    // Safely parse loyalty JSON string - only after mount to prevent hydration mismatch
    let loyaltyData = {}
    if (hasMounted) {
        try {
            loyaltyData = JSON.parse(customer.data?.c_loyaltyData || '{}')
        } catch (e) {
            console.error('Failed to parse loyalty data', e)
        }
    }

    const membershipStatus = loyaltyData.membershipStatus || {}
    const pointsAndCash = loyaltyData.pointsAndCashDiscount || {}

    const points = pointsAndCash.nonQualifyingPointsBalance ?? 0
    const statusKey = membershipStatus.status?.toLowerCase() ?? 'creator'
    const statusMessage = statusLevels.find((level) => level.key === statusKey)?.message
    const status = intl.formatMessage(statusMessage).toUpperCase()
    const progressValue = membershipStatus.maxStatusPercentage ?? 0
    const progressBarEnabled = loyaltyData?.progressBarEnabled
    const nextTierStatusName = loyaltyData?.nextTier?.statusName

    const getReachedStatuses = (currentStatus) => {
        const index = statusLevels.findIndex(
            (level) => level.key.toLowerCase() === currentStatus.toLowerCase()
        )
        return index >= 0 ? statusLevels.slice(0, index + 1).map((level) => level.key) : []
    }

    return (
        <Box bg={isAccountDrawer ? 'warmWhite' : 'transparent'} p={isAccountDrawer ? 4 : 0} mt={4}>
            {!isAccountDrawer && loyaltyId && (
                <Text variant="barcodeText">
                    <FormattedMessage
                        id="barcode.loyalt.id"
                        defaultMessage="Loyalty ID: {loyaltyId}"
                        values={{loyaltyId}}
                    />
                </Text>
            )}
            {isAccountDrawer && (
                <Text variant="accountInfo">
                    {nextTierStatusName ? (
                        isGarage ? (
                            <FormattedMessage
                                defaultMessage="You're on your way to Collectif {status} status!"
                                id="account_modal.collectif_journey.garage"
                                values={{
                                    status: (
                                        <Text as="span" sx={{textTransform: 'capitalize'}}>
                                            {nextTierStatusName.toLowerCase()}
                                        </Text>
                                    )
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="You're on your way to Collectif {status} status!"
                                id="account_modal.collectif_journey.dynamite"
                                values={{
                                    status: (
                                        <Text as="span" sx={{textTransform: 'capitalize'}}>
                                            {nextTierStatusName.toLowerCase()}
                                        </Text>
                                    )
                                }}
                            />
                        )
                    ) : isGarage ? (
                        <FormattedMessage
                            defaultMessage="You're Collectif {status} status!"
                            id="account_modal.collectif_journey_final_status.garage"
                            values={{
                                status: (
                                    <Text as="span" sx={{textTransform: 'capitalize'}}>
                                        {status.toLowerCase()}
                                    </Text>
                                )
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="You're Collectif {status} status!"
                            id="account_modal.collectif_journey_final_status.dynamite"
                            values={{
                                status: (
                                    <Text as="span" sx={{textTransform: 'capitalize'}}>
                                        {status.toLowerCase()}
                                    </Text>
                                )
                            }}
                        />
                    )}
                </Text>
            )}
            <Text mt={3} fontSize="sm" lineHeight="1">
                {isGarage ? (
                    <FormattedMessage
                        defaultMessage="Your points"
                        id="account_modal.your_points.garage"
                    />
                ) : (
                    <FormattedMessage
                        defaultMessage="Your points"
                        id="account_modal.your_points.dynamite"
                    />
                )}
            </Text>
            <Text variant="accountStatus" mb={progressBarEnabled && 6}>
                {points}
            </Text>
            {progressBarEnabled && (
                <>
                    <Text mt={4} variant="accountInfoSmall" color="grey.800">
                        {isGarage ? (
                            <FormattedMessage
                                defaultMessage="Your Collectif status:"
                                id="account_modal.collectif_status_label.garage"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Your Collectif status:"
                                id="account_modal.collectif_status_label.dynamite"
                            />
                        )}
                    </Text>
                    <Text textStyle="webH3">{status}</Text>

                    <Box mt={2}>
                        <Box
                            w="100%"
                            bg="#E2DFDA"
                            borderRadius="unset"
                            overflow="hidden"
                            height="6px"
                            position="relative"
                            zIndex="1"
                        >
                            <Box
                                height="100%"
                                width={progressValue > 0 ? `${progressValue + 10}%` : 0}
                                bg="progressPink.500"
                                transition="width 0.3s ease"
                                borderRadius="inherit"
                            />
                        </Box>
                    </Box>

                    <HStack justify="space-between" mt={2}>
                        {statusLevels.map(({key, message}) => {
                            const showSymbol = getReachedStatuses(status).includes(key)
                            return (
                                <Text
                                    key={key}
                                    position="relative"
                                    variant="textWithSymbol"
                                    sx={
                                        showGauge && showSymbol
                                            ? {
                                                  '&::after': {
                                                      content: '"\\25A0"',
                                                      position: 'absolute',
                                                      top: '-24px',
                                                      left: '50%',
                                                      transform: 'translateX(-50%) rotate(45deg)'
                                                  }
                                              }
                                            : {}
                                    }
                                >
                                    {intl.formatMessage(message)}
                                </Text>
                            )
                        })}
                    </HStack>
                </>
            )}
        </Box>
    )
}

LoyaltyPoints.propTypes = {
    isAccountDrawer: PropTypes.bool,
    loyaltyId: PropTypes.string,
    showGauge: PropTypes.bool
}

export default LoyaltyPoints
